import Api from '../apis/Api';
import GLU from '../glu2.js/src/index';

import AppConfig from '../appConfig';

class VisualReportDataSource extends GLU.DataSource {
    static get name() {
        return 'VisualReportDataSource';
    }

    static getInstance() {
        return new VisualReportDataSource();
    }

    constructor() {
        super(() => false);
        /** @type {import('../types').VisualReport[]} */
        this._visualReports = [];
    }

    /** @returns {import('../types').VisualReport[]}  */
    get visualReports() {
        return this._visualReports;
    }

    /**
     * @returns {Promise<import('../types').VisualReport[]>}
     */
    loadVisualReports = () =>
        new Promise((resolve, reject) => {
            Api.visualReport.getVisualReports().then(response => {
                if (response.body) {
                    this._visualReports = response.body.map(this.parseVisualReportResponse);
                }
                resolve(this._visualReports);
            }, reject);
        });

    loadVisualReportsHydrated = () =>
        new Promise((resolve, reject) => {
            Api.visualReport
                .getVisualReports({
                    fetchAddressIfMissing: true,
                })
                .then(response => {
                    if (response.body) {
                        this._visualReports = response.body.map(this.parseVisualReportResponse);
                    } else {
                        this._visualReports = [];
                    }
                    resolve(this._visualReports);
                }, reject);
        });

    /**
     * @param {import('../types').VisualReport} visualReport
     */
    saveVisualReport = visualReport => {
        this._visualReports.unshift(visualReport);
    };

    /**
     * @param {object} payload
     * @param {string} payload.title
     * @param {number} payload.report
     * @param {number[]} payload.selection
     * @param {string} payload.profile
     * @param {number} param0.listId
     */
    updateVisualReport(payload) {
        const {
            reportId,
            title,
            selection,
            profile,
            listId,
            status,
            notes,
            point,
            facilityId,
            pointType,
            address,
            polygon,
        } = payload;

        const fullPayload = {
            reportId,
            title,
            listId,
            status,
            notes,
            selection,
            profile,
            polygon,
            definition: {
                selection,
                profile,
                point,
                facilityId,
                pointType,
                address,
            },
        };

        return new Promise((resolve, reject) => {
            Api.visualReport.updateVisualReport({ payload: fullPayload }).then(response => {
                const visualReport = this.parseVisualReportResponse(response.body);
                const index = this._visualReports.findIndex(r => r.id === visualReport.id);
                if (index > -1) {
                    this._visualReports[index] = visualReport;
                }
                resolve(visualReport);
            }, reject);
        });
    }

    /**
     * @param {import('../types').VisualReport} visualReport
     */
    updateLocalVisualReport(payload) {
        const index = this._visualReports.findIndex(r => r.id === payload.visualReport.id);
        if (index > -1) {
            this._visualReports[index] = payload.visualReport;
        }
    }

    /**
     * @param {number} reportId
     */
    deleteVisualReport(reportId) {
        return new Promise((resolve, reject) => {
            Api.visualReport
                .deleteVisualReport({
                    payload: {
                        reportId,
                    },
                })
                .then(() => {
                    // Remove deleted user location
                    const index = this._visualReports.findIndex(r => r.id === reportId);
                    if (index > -1) {
                        this._visualReports.splice(index, 1);
                    }
                    resolve(this._visualReports);
                }, reject);
        });
    }

    /**
     * @param {import('../types').VisualReportResponse} visualReportResponse
     * @returns {import('../types').VisualReport}
     */
    parseVisualReportResponse = visualReportResponse => ({
        id: visualReportResponse.id,
        title: visualReportResponse.title,
        point: visualReportResponse.definition && visualReportResponse.definition.point,
        selection: visualReportResponse.definition && visualReportResponse.definition.selection,
        profile: visualReportResponse.definition && visualReportResponse.definition.profile,
        polygon: visualReportResponse.definition && visualReportResponse.definition.polygon,
        pointType: visualReportResponse.definition && visualReportResponse.definition.pointType,
        facilityId: visualReportResponse.definition && visualReportResponse.definition.facilityId,
        address: visualReportResponse.definition && visualReportResponse.definition.address,
        reportType: visualReportResponse.type,
        listId: visualReportResponse.list_id,
        status: visualReportResponse.status,
        notes: visualReportResponse.notes,
        dealListName: visualReportResponse.deal_list_name,
    });
}

export default VisualReportDataSource;
